import { useEffect, useRef, useState } from "react";
import {
  formatSiengeQuotaMessage,
  SIENGE_QUOTA_ERROR_CODE,
  SERVICE_RESTARTED_ERROR_CODE,
  SERVICE_RESTARTED_MESSAGE,
  unprocessedSources,
} from "@contexts/projects/updateJobs";

/**
 * Acompanhamento visual da execução do pipeline.
 *
 * As etapas vêm do data-pipeline, que publica `stage` em `update_jobs` antes de cada bloco
 * (`update_jobs.py::UPDATE_JOB_STAGES`). O MEGA passa por `extract`/`transform`/`load`; o
 * Sienge por `extract`/`transform`/`parsing`/`stage`/`validate`/`publish`. Aqui elas são
 * AGRUPADAS nos três passos que fazem sentido para quem olha a tela — baixar, processar,
 * carregar — mais o refresh final. O agrupamento vive no front de propósito: o pipeline pode
 * ganhar etapas novas sem quebrar esta UI.
 *
 * As etapas finais do Sienge (`stage`, `validate`, `publish`) entram em "Carregando dados"
 * porque é o que elas são: a publicação do snapshot. Sem isso elas caíam no fallback de
 * etapa desconhecida e o painel VOLTAVA para "Baixando dados" no fim do run.
 */
const STEPS = [
  { key: 'download', label: 'Baixando dados', stages: ['extract'] },
  { key: 'process', label: 'Processando dados', stages: ['transform', 'parsing'] },
  { key: 'load', label: 'Carregando dados', stages: ['clean', 'load', 'stage', 'validate', 'publish'] },
  { key: 'refresh', label: 'Atualizando a tela', stages: [] },
]

// `blocked` é terminal como `failed`/`succeeded`: o pipeline não vai retentar sozinho (cota
// Sienge esgotada, por exemplo — ver `SIENGE_QUOTA_ERROR_CODE`). A execução parou e só volta a
// se mover com uma nova solicitação do usuário depois da renovação.
const TERMINAL = ['succeeded', 'failed', 'blocked']
const POLL_MS = 5000

// Índice do último passo concreto (o `refresh` não é etapa do pipeline). Uma etapa que o
// front não conhece só pode ter sido ACRESCENTADA ao pipeline, e etapas são acrescentadas no
// fim — tratá-la como o passo 1 fazia o painel parecer travado no começo justamente quando o
// run estava terminando.
const LAST_PIPELINE_STEP = STEPS.length - 2

const stepStateFor = (job, step, index) => {
  if (!job) return 'pending'
  if (job.status === 'failed' || job.status === 'blocked') {
    // Falhou/bloqueado: o passo onde parou fica marcado como erro, os anteriores como concluídos.
    // Sem `stage` nenhum o job caiu antes do primeiro bloco, então o erro é no passo 1; já um
    // nome de etapa que o front não conhece veio do fim do pipeline (ver LAST_PIPELINE_STEP).
    if (!job.stage) return index === 0 ? 'error' : 'pending'
    const found = STEPS.findIndex(s => s.stages.includes(job.stage))
    const errorIndex = found === -1 ? LAST_PIPELINE_STEP : found
    if (index < errorIndex) return 'done'
    return index === errorIndex ? 'error' : 'pending'
  }
  if (job.status === 'succeeded') return 'done'
  if (job.status === 'queued') return index === 0 ? 'waiting' : 'pending'

  // running
  if (step.key === 'refresh') return 'pending'
  if (!job.stage) return index === 0 ? 'active' : 'pending'
  const found = STEPS.findIndex(s => s.stages.includes(job.stage))
  const activeIndex = found === -1 ? LAST_PIPELINE_STEP : found
  if (index < activeIndex) return 'done'
  return index === activeIndex ? 'active' : 'pending'
}

const STATE_LABEL = {
  pending: '',
  waiting: 'na fila',
  active: 'em andamento',
  done: 'concluído',
  error: 'falhou',
}

/**
 * fetchJob: `async () => ({ ok, job })`. É parâmetro e não uma chamada fixa porque as duas
 *   telas leem o job de lugares diferentes: a atualização de rotina usa
 *   `GET /project/updateJob`, que resolve a obra em `wbs_buildings`; a PRIMEIRA execução usa
 *   `GET /project/bootstrapBuilding`, porque antes da ingestão `wbs_buildings` não existe e o
 *   outro endpoint responderia 404 exatamente quando a barra de progresso mais importa.
 * onFinished / onTerminal: cada callback informado é chamado uma única vez quando o job chega
 *   a estado terminal. É o gancho do "auto refresh": quem usa decide se recarrega a listagem,
 *   a página ou fecha o modal.
 *
 * ⚠️ O componente SEMPRE faz o próprio polling, mesmo onde a tela ao redor já consulta o job.
 * A versão anterior tinha um "modo controlado" que só renderizava o que o Header lhe passava —
 * e o laço do Header é uma cadeia de setTimeout que se auto-agenda: ela termina DEFINITIVAMENTE
 * em qualquer hipótese que não seja "job ativo" (resposta não-ok, falha de rede, blip nas
 * dependências do efeito, throttling de aba em segundo plano), sem nada que a reinicie. Em
 * produção isso congelou o painel em "Processando dados" enquanto o pipeline seguiu até o fim.
 * Duplicar a consulta custa uma requisição a cada 5s enquanto o modal está aberto; congelar a
 * tela custa a confiança no acompanhamento.
 */
// Falhas transitórias não podem matar o acompanhamento: o laço continua tentando e só desiste
// depois de várias falhas seguidas, aí sim mostrando erro em vez de um progresso mentiroso.
const MAX_CONSECUTIVE_FAILURES = 5
// Rede de segurança: mesmo sem estado terminal, o laço não roda para sempre.
const MAX_POLL_MS = 60 * 60 * 1000

// `blockedNotices`: mapa opcional `error_code -> texto pt-BR` para motivos de bloqueio que NÃO
// são erro de verdade (ex.: a Diretoria já rodou hoje — `director_cycle_already_completed_today`,
// Task 16D fix round 2). Quando o `error_code` do job bate com uma entrada, o texto substitui o
// genérico "A execução foi bloqueada (código)." e usa `update-stages-note` (tom neutro) em vez de
// `update-stages-error` (vermelho) — o componente continua genérico, sem vocabulário de domínio
// hardcoded; quem sabe que código é meramente informativo é o chamador (`DirectorUpdateButton`).
const UpdateJobStages = ({
  fetchJob,
  onFinished,
  onTerminal,
  initialJob = null,
  quotaBlockedUntil = null,
  blockedNotices = {},
}) => {
  const [job, setJob] = useState(initialJob)
  const [pollIssue, setPollIssue] = useState(null)
  const [pollCycle, setPollCycle] = useState(0)
  const finishedRef = useRef(false)
  const fetchJobRef = useRef(fetchJob)
  const onFinishedRef = useRef(onFinished)
  const onTerminalRef = useRef(onTerminal)

  fetchJobRef.current = fetchJob
  onFinishedRef.current = onFinished
  onTerminalRef.current = onTerminal

  useEffect(() => {
    let cancelled = false
    let timer = null
    let failures = 0
    const startedAt = Date.now()

    const tick = async () => {
      if (Date.now() - startedAt >= MAX_POLL_MS) {
        setPollIssue({
          type: 'timeout',
          message: 'A consulta automática atingiu o tempo limite. O job pode continuar em execução.',
        })
        return
      }

      let res
      try {
        res = await fetchJobRef.current()
      } catch {
        res = null
      }
      if (cancelled) return

      if (!res?.ok) {
        failures += 1
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          setPollIssue({
            type: 'error',
            message: res?.message || 'Não foi possível consultar o andamento.',
          })
          return
        }
        timer = setTimeout(tick, POLL_MS)
        return
      }

      failures = 0
      setPollIssue(null)
      setJob(res.job)

      if (res.job && TERMINAL.includes(res.job.status)) {
        // Guarda de uma-vez-só: sem ela, um poll em vôo depois do terminal chamaria
        // onFinished de novo e a tela recarregaria em loop.
        if (!finishedRef.current) {
          finishedRef.current = true
          onFinishedRef.current?.(res.job)
          onTerminalRef.current?.(res.job)
        }
        return
      }
      if (Date.now() - startedAt >= MAX_POLL_MS) {
        setPollIssue({
          type: 'timeout',
          message: 'A consulta automática atingiu o tempo limite. O job pode continuar em execução.',
        })
        return
      }
      timer = setTimeout(tick, POLL_MS)
    }

    tick()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollCycle])

  const retryPolling = () => {
    setPollIssue(null)
    setPollCycle(cycle => cycle + 1)
  }

  if (!job && !pollIssue) {
    return <p role="status" aria-live="polite">Consultando andamento...</p>
  }

  const failed = job?.status === 'failed'
  const succeeded = job?.status === 'succeeded'
  const blocked = job?.status === 'blocked'
  const quotaBlocked = blocked && job?.error_code === SIENGE_QUOTA_ERROR_CODE
  // 21/09: a execução segue com o que o ERP entregou, então "concluída" pode conviver com
  // fontes que não vieram. Sem esta lista, o usuário vê um número velho (ou vazio) sem
  // nenhuma pista de que ele não foi atualizado nesta execução — e abre chamado.
  const missing = unprocessedSources(job)

  return (
    <div className="update-stages">
      {job && (
        <div role="status" aria-live="polite">
          <ol className="update-stages-list">
            {STEPS.map((step, index) => {
              const state = stepStateFor(job, step, index)
              return (
                <li key={step.key} className={`update-stage update-stage--${state}`}>
                  <span className="update-stage-dot" aria-hidden="true" />
                  <span className="update-stage-label">{step.label}</span>
                  {STATE_LABEL[state] && (
                    <span className="update-stage-state">{STATE_LABEL[state]}</span>
                  )}
                </li>
              )
            })}
          </ol>

          {job.status === 'queued' && (
            <p className="update-stages-note">Na fila. A execução começa em instantes.</p>
          )}
          {succeeded && (
            <p className="update-stages-note">
              {job.bootstrap
                ? 'Primeira ingestão concluída. A obra e os acessos já estão disponíveis.'
                : missing.length > 0
                  ? 'Atualização concluída parcialmente.'
                  : 'Atualização concluída.'}
            </p>
          )}
          {failed && (
            <p className="update-stages-error">
              {job.error_code === SERVICE_RESTARTED_ERROR_CODE
                ? SERVICE_RESTARTED_MESSAGE
                : `A execução falhou${job.error_code ? ` (${job.error_code})` : ''}. Você pode tentar novamente.`}
            </p>
          )}
          {blocked && !quotaBlocked && job.error_code && blockedNotices[job.error_code] ? (
            <p className="update-stages-note">{blockedNotices[job.error_code]}</p>
          ) : blocked && (
            <p className="update-stages-error">
              {quotaBlocked
                ? formatSiengeQuotaMessage(quotaBlockedUntil ?? job.sienge_quota_blocked_until)
                : `A execução foi bloqueada${job.error_code ? ` (${job.error_code})` : ''}.`}
            </p>
          )}
          {missing.length > 0 && (
            <div className="update-stages-note update-stages-missing">
              <p>Informações não atualizadas nesta execução:</p>
              <ul>
                {missing.map((source) => (
                  <li key={source.route}>
                    <strong>{source.label}</strong> — {source.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {pollIssue && (
        <div className="update-stages-error" role="alert">
          <p>
            {pollIssue.message} Consulte novamente para verificar o estado atual.
          </p>
          <button type="button" className="button" onClick={retryPolling}>
            Consultar novamente
          </button>
        </div>
      )}
    </div>
  );
};

export default UpdateJobStages;
